import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ImageSquare, Plus, CircleNotch, Tag, Check, Trash, UserFocus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

// Photo & document archive with "Who is in this photo?" community tagging.
export function PhotoArchive({ scopeType, scopeId, scopeName }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState(null);
  const load = () => api.get("/photos", { params: { scope_type: scopeType, scope_id: scopeId } })
    .then((r) => setPhotos(r.data.photos || [])).catch(() => setPhotos([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopeType, scopeId]);

  return (
    <div data-testid="photo-archive">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-xl font-medium">Photo archive</h3>
          <p className="text-sm text-muted-foreground">Class, staff & event photos — help identify the people in them.</p>
        </div>
        {user ? <UploadPhotoDialog scopeType={scopeType} scopeId={scopeId} scopeName={scopeName} onDone={load} />
          : <span className="text-xs text-muted-foreground">Sign in to add photos</span>}
      </div>

      {photos === null ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        : photos.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-dashed border-border/60 p-8 text-center">
            <ImageSquare size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No photos yet. Upload an old class or staff photograph to start the archive.</p>
          </div>
        ) : (
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {photos.map((ph) => <PhotoCard key={ph.id} ph={ph} user={user} onChange={load} />)}
          </div>
        )}
    </div>
  );
}

function PhotoCard({ ph, user, onChange }) {
  const isOwner = user && (user.id === ph.contributor_id || user.role === "admin");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const addName = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post(`/photos/${ph.id}/identify`, { name: name.trim() }); setName(""); toast.success("Thanks — suggestion added"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const confirm = async (tagId, ok) => {
    try { await api.patch(`/photos/${ph.id}/identify/${tagId}?confirm=${ok}`); onChange(); } catch { toast.error("Failed"); }
  };
  const del = async () => { try { await api.delete(`/photos/${ph.id}`); onChange(); } catch { toast.error("Failed"); } };

  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm" data-testid={`photo-${ph.id}`}>
      <img src={fileUrl(ph.file_id)} alt={ph.caption || "Archive photo"} className="aspect-video w-full object-cover" />
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {ph.caption && <p className="truncate text-sm font-medium">{ph.caption}</p>}
            <p className="text-xs text-muted-foreground">{[ph.approx_date, ph.location].filter(Boolean).join(" · ")}{ph.contributor_name ? ` · by ${ph.contributor_name}` : ""}</p>
          </div>
          {isOwner && <button onClick={del} className="text-muted-foreground hover:text-destructive" data-testid={`photo-delete-${ph.id}`}><Trash size={14} /></button>}
        </div>

        <div className="mt-2 border-t border-border/60 pt-2">
          <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground"><UserFocus size={13} /> Who is in this photo?</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {(ph.people_tags || []).length === 0 && <span className="text-xs text-muted-foreground/70">No one identified yet.</span>}
            {(ph.people_tags || []).map((t) => (
              <span key={t.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${t.status === "confirmed" ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid={`phototag-${t.id}`}>
                <Tag size={11} /> {t.name}{t.status === "confirmed" ? <Check size={11} /> : " (suggested)"}
                {isOwner && t.status !== "confirmed" && <button onClick={() => confirm(t.id, true)} className="ml-1 underline" data-testid={`confirm-tag-${t.id}`}>confirm</button>}
              </span>
            ))}
          </div>
          {user && (
            <div className="mt-2 flex items-center gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Suggest a name…" className="h-8 text-xs" data-testid={`identify-input-${ph.id}`} onKeyDown={(e) => e.key === "Enter" && addName()} />
              <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={addName} disabled={busy} data-testid={`identify-btn-${ph.id}`}>{busy ? "…" : "Add"}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadPhotoDialog({ scopeType, scopeId, scopeName, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ caption: "", approx_date: "", location: "", permission: false });
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo"); return; }
    if (!f.permission) { toast.error("Please confirm you have permission to share this photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/photos", { scope_type: scopeType, scope_id: scopeId, file_id: data.id, ...f });
      toast.success("Photo added to the archive");
      setOpen(false); setFile(null); setF({ caption: "", approx_date: "", location: "", permission: false });
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-full gap-1.5" data-testid="upload-photo-open"><Plus size={15} /> Add photo</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="upload-photo-dialog">
        <DialogHeader><DialogTitle>Add a photo</DialogTitle><DialogDescription>{scopeName ? <>Adding to <strong>{scopeName}</strong>. </> : null}Old class, staff, event or graduation photos welcome.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div onClick={() => fileRef.current.click()} className="grid h-36 cursor-pointer place-items-center rounded-xl border-2 border-dashed border-border bg-muted/30 text-center" data-testid="photo-drop">
            {file ? <img src={URL.createObjectURL(file)} alt="" className="h-full w-full rounded-xl object-contain" /> : <span className="text-sm text-muted-foreground"><ImageSquare size={26} className="mx-auto mb-1" /> Click to choose a photo</span>}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => setFile(e.target.files[0])} data-testid="photo-file" />
          </div>
          <div><Label className="text-xs">Caption</Label><Input value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} className="mt-1" placeholder="e.g. Staff photo, 1978" data-testid="photo-caption" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Approx. date</Label><Input value={f.approx_date} onChange={(e) => setF({ ...f, approx_date: e.target.value })} className="mt-1" placeholder="1978" /></div>
            <div><Label className="text-xs">Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="mt-1" /></div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={f.permission} onChange={(e) => setF({ ...f, permission: e.target.checked })} data-testid="photo-permission" /> I have permission to share this photo.</label>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="photo-submit">{busy ? <CircleNotch size={15} className="animate-spin" /> : <Plus size={15} />} {busy ? "Uploading…" : "Add photo"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoArchive;
